/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ErrorResponseModel } from '../../../../../core/models';
import { Injectable, OnDestroy } from '@angular/core';
import { LockApiService } from '../../http';
import { LockModel } from '../../models';
import { LogService, NotifyService, UserService, UtilsService } from '../../../../../core/services';
import { Subject } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class LockService implements OnDestroy {
    /**
     * Locks emitter
     */
    public locksEmitter: Subject<any> = new Subject<any>();

    /**
     * Store all locks from the socket data.
     * @private
     */
    private _locks: LockModel;

    /**
     * Stored Timestamp of the last socket event.
     * @private
     */
    private timeStamp: number;

    /**
     * Stored intervalId for use in OnDestroy.
     * @private
     */
    private intervalId: any;

    constructor(
        private lockApiService: LockApiService,
        private logService: LogService,
        private notifyService: NotifyService,
        private userService: UserService,
        private utilsService: UtilsService
    ) {
        this.onInitialise();
        this.subscribeToUserService();
    }

    onInitialise(): void {
        this.timeStamp = new Date().getTime();
        this.intervalId = setInterval((): void => {
            if (Math.abs(this.timeStamp - new Date().getTime()) > 15000) {
                this.getDataFromApi();
                this.logService.debug('LockService:onInitialise', 'Socket data out of date, making an API call.');
            }
        }, 15000);
    }

    ngOnDestroy(): void {
        this.tearDownService();
    }

    set locks(locks: LockModel) {
        this.timeStamp = new Date().getTime();
        if (JSON.stringify(locks) !== JSON.stringify(this._locks)) {
            this._locks = locks;
            this.locksEmitter.next(this.locks);
        }
    }

    get locks(): LockModel {
        return this._locks;
    }

    /**
     * Force set a lock state. (resolves timing issues).
     * @param property
     * @param state
     */
    public setLockStateByProperty(property: 'apiLock' | 'transactionLocked' | 'userLock', state: boolean): void {
        this._locks[property] = state;
    }

    /**
     * Get lock state by property.
     * @param property
     */
    public getLockStateByProperty(property: 'apiLock' | 'transactionLocked' | 'userLock'): boolean {
        if (this._locks.hasOwnProperty(property)) {
            return this._locks[property];
        }
    }

    /**
     * Check to see if there is an active transaction on this box.
     * @private
     */
    private getDataFromApi(): void {
        this.lockApiService.getLockStatus()
            .subscribe({
                next: (response: any): void => {
                    this.locks = response;
                },
                error: (error: ErrorResponseModel): void => {
                    const htmlDebugMessage = this.utilsService.formatCapturedError(error);
                    this.notifyService.error(htmlDebugMessage, { static: true });
                }
            });
    }

    /**
     * Subscribe to user service to detect logout.
     * @private
     */
    private subscribeToUserService(): void {
        this.userService.logOutEmitter
            .subscribe({
                next: (state: boolean): void => {
                    if (state) {
                        this.tearDownService();
                    }
                }
            });
    }

    /**
     * Tear down service.
     * @private
     */
    private tearDownService(): void {
        clearInterval(this.intervalId);
        this._locks = undefined;
        this.logService.debug('LockService:tearDownService', 'Service cleaned up');
    }
}
